import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Flex, Heading, Input, Notice, RadioCard } from '@buffer-mono/popcorn'

import { ICON_COLORS } from '../../constants'
import type { StepProps } from '../../types'
import { StepActions } from '../StepActions/StepActions'

import styles from './SingleSelectStep.module.css'

/**
 * @hardcoded Slug-to-emoji map. Adding or renaming an option in the API silently
 *   loses its icon on the frontend.
 * @fix Add `iconUrl` to `SurveyStepOption` in the GraphQL schema and render from
 *   the API value. Do not extend this map.
 */
const OPTION_EMOJIS: Record<string, string> = {
  'solo-creator': '\u{1F44B}',
  'small-business-owner': '\u{1F4AA}',
  'company-marketing-team': '\u{1F3D8}\u{FE0F}',
  'freelancer-consultant': '\u{2B50}',
  'marketing-agency': '\u{1F3AF}',
  'non-profit': '\u{1F3C6}',
  other: '\u{1F984}',
}

export function SingleSelectStep({
  step,
  initialAnswers,
  initialTextValue,
  disabled,
  loading,
  error,
  onDismissError,
  onSubmit,
  onSkip,
}: StepProps): JSX.Element {
  const [selected, setSelected] = useState<string>(initialAnswers[0] ?? '')
  const [otherText, setOtherText] = useState(initialTextValue ?? '')
  const otherInputRef = useRef<HTMLInputElement>(null)
  /**
   * @hardcoded Implicit contract on slug name 'other'.
   * @fix Add `allowsTextInput` to `SurveyStepOption` and replace this slug check.
   */
  const isOtherSelected = selected === 'other'
  const isSubmitDisabled = !selected || (isOtherSelected && !otherText.trim())

  useEffect(() => {
    if (isOtherSelected) {
      otherInputRef.current?.focus()
    }
  }, [isOtherSelected])

  return (
    <div className={styles.step}>
      <Heading as="h1" size="xl" align="center">
        {step.title}
      </Heading>

      {error && (
        <Notice variant="error" onDismiss={onDismissError}>
          <Notice.Text>{error}</Notice.Text>
        </Notice>
      )}

      <RadioCard.Group
        value={selected}
        onValueChange={(value: string): void => setSelected(value)}
        className={styles.options}
      >
        {step.options.map((option, index) => {
          const emoji = OPTION_EMOJIS[option.slug]
          const isOther = option.slug === 'other'
          return (
            <RadioCard
              key={option.slug}
              value={option.slug}
              padding="sm"
              className={styles.card}
              disabled={disabled}
            >
              <Flex direction="column" className={styles.cardContent}>
                <Flex align="center" gap="sm">
                  {emoji && (
                    <div
                      className={styles.optionIconWrapper}
                      style={{
                        backgroundColor:
                          ICON_COLORS[index % ICON_COLORS.length],
                      }}
                    >
                      <span className={styles.optionIcon} aria-hidden="true">
                        {emoji}
                      </span>
                    </div>
                  )}
                  <span className={styles.cardLabel}>{option.label}</span>
                </Flex>
                {isOther && (
                  <div
                    className={styles.otherExpand}
                    data-open={isOtherSelected || undefined}
                  >
                    <div className={styles.otherExpandInner}>
                      <Input
                        ref={otherInputRef}
                        placeholder="Please specify..."
                        value={otherText}
                        onChange={(e): void => setOtherText(e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                )}
              </Flex>
            </RadioCard>
          )
        })}
      </RadioCard.Group>

      <StepActions
        onContinue={(): void =>
          onSubmit([selected], isOtherSelected ? otherText.trim() : undefined)
        }
        disabled={isSubmitDisabled}
        controlsDisabled={disabled}
        loading={loading}
        isSkippable={step.isSkippable}
        onSkip={onSkip}
      />
    </div>
  )
}
